import axios from 'axios';


const baseURL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const client = axios.create({
    baseURL,
    withCredentials: true,
    xsrfCookieName: 'csrftoken',
    xsrfHeaderName: 'X-CSRFToken',
});


function decodeFilenameCandidate(value, fallback) {
    if (!value) {
        return fallback;
    }

    let decoded = value.trim().replace(/^UTF-8''/i, '');
    for (let attempt = 0; attempt < 2; attempt += 1) {
        if (!decoded.includes('%')) {
            break;
        }
        try {
            const nextValue = decodeURIComponent(decoded);
            if (nextValue === decoded) {
                break;
            }
            decoded = nextValue;
        } catch (_error) {
            break;
        }
    }

    decoded = decoded.split('/').pop()?.split('\\').pop() || fallback;
    return decoded || fallback;
}


function extractFilename(disposition, fallback) {
    const filenameStarMatch = (disposition || '').match(/filename\*=([^;]+)/i);
    if (filenameStarMatch?.[1]) {
        return decodeFilenameCandidate(filenameStarMatch[1], fallback);
    }

    const filenameMatch = (disposition || '').match(/filename="?([^"]+)"?/i);
    return decodeFilenameCandidate(filenameMatch?.[1], fallback);
}


function filenameFromUrl(url, fallback) {
    try {
        const parsed = new URL(url, window.location.origin);
        const filename = parsed.pathname.split('/').pop();
        return decodeFilenameCandidate(filename, fallback);
    } catch (_error) {
        return fallback;
    }
}


async function downloadBlobResponse(requestPromise, fallbackFilename) {
    const response = await requestPromise;
    const filename = extractFilename(response.headers['content-disposition'], fallbackFilename);
    const blob = new Blob([response.data], { type: 'application/octet-stream' });

    if (window.navigator?.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename);
        return;
    }

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = blobUrl;
    link.download = filename;
    link.setAttribute('download', filename);
    link.style.display = 'none';
    document.body.appendChild(link);
    window.requestAnimationFrame(() => {
        link.click();
    });

    window.setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
    }, 60_000);
}


export function apiGet(path, config = {}) {
    return client.get(path, config);
}


export function isUnauthorizedError(error) {
    return error?.response?.status === 401;
}


export async function ensureCsrfCookie() {
    const response = await client.get('/api/v1/auth/csrf/');
    return response.data;
}


export async function loginUser(credentials) {
    const response = await client.post('/api/v1/auth/login/', credentials);
    return response.data;
}


export async function logoutUser() {
    const response = await client.post('/api/v1/auth/logout/');
    return response.data;
}


export async function loadCurrentUser() {
    const response = await client.get('/api/v1/me/');
    return response.data;
}


export async function loadProfile() {
    const response = await client.get('/api/v1/profile/');
    return response.data;
}


export async function updateProfile(formData) {
    const response = await client.post('/api/v1/profile/update/', formData);
    return response.data;
}


export async function changePassword(payload) {
    const response = await client.post('/api/v1/profile/password/', payload);
    return response.data;
}


export async function loadLegacyDashboard(params = {}) {
    const response = await client.get('/api/v1/dashboard/legacy/', { params });
    return response.data;
}


export async function loadDashboardData() {
    const [user, dashboard] = await Promise.all([
        loadCurrentUser().then((data) => ({ data })),
        loadLegacyDashboard().then((data) => ({ data })),
    ]);

    return {
        user: user.data,
        dashboard: dashboard.data,
    };
}


export async function loadMetadata() {
    const response = await client.get('/api/v1/metadata/');
    return response.data;
}


export async function loadModules(params) {
    const response = await client.get('/api/v1/modules/', { params });
    return response.data;
}


export async function downloadSearchExport(params = {}) {
    await downloadBlobResponse(
        client.get('/api/v1/search/export/', {
            params,
            responseType: 'blob',
        }),
        'hyosung_search_results.xlsx',
    );
}


export async function updateSearchModule(moduleId, payload) {
    const response = await client.post(`/api/v1/search/modules/${moduleId}/update/`, payload);
    return response.data;
}


export async function importSearchRepairResults(formData) {
    const response = await client.post('/api/v1/search/repair-import/', formData);
    return response.data;
}


export async function loadSearchModuleDetail(moduleId) {
    const response = await client.get(`/api/v1/search/modules/${moduleId}/`);
    return response.data;
}


export async function loadSearchRequestOptions(query, selected = '') {
    const response = await client.get('/api/v1/search/request-options/', {
        params: {
            q: query,
            selected,
        },
    });
    return response.data;
}


export async function downloadSearchRepairTemplate() {
    await downloadBlobResponse(
        client.get('/api/v1/search/repair-import/template/', {
            responseType: 'blob',
        }),
        'hyosung_search_repair_template.xlsx',
    );
}


export async function downloadFileByUrl(url, fallbackFilename = 'hyosung-file.bin') {
    await downloadBlobResponse(
        client.get(url, {
            responseType: 'blob',
        }),
        filenameFromUrl(url, fallbackFilename),
    );
}


export async function loadRequestQueue(queue, params = {}) {
    const response = await client.get('/api/v1/requests/queues/', {
        params: {
            queue,
            page: 1,
            page_size: 25,
            ...params,
        },
    });
    return response.data;
}


export async function loadRequestDetail(slug) {
    const response = await client.get(`/api/v1/requests/${slug}/`);
    return response.data;
}


export async function loadEngineerStatistics(params) {
    const response = await client.get('/api/v1/engineers/statistics/', { params });
    return response.data;
}


export async function loadEngineerStatisticsDetail(params) {
    const response = await client.get('/api/v1/engineers/statistics/detail/', { params });
    return response.data;
}


export async function downloadEngineerStatisticsExport(params = {}) {
    await downloadBlobResponse(
        client.get('/api/v1/engineers/statistics/export/', {
            params,
            responseType: 'blob',
        }),
        'hyosung_engineer_statistics.xlsx',
    );
}


export async function loadMotivation(params) {
    const response = await client.get('/api/v1/engineers/motivation/', { params });
    return response.data;
}


export async function downloadMotivationExport(params = {}) {
    await downloadBlobResponse(
        client.get('/api/v1/engineers/motivation/export/', {
            params,
            responseType: 'blob',
        }),
        'Премия Hyosung.xlsx',
    );
}


export async function saveMotivationEngineerInn(payload) {
    const response = await client.post('/api/v1/engineers/motivation/inns/', payload);
    return response.data;
}


export async function saveMotivationDeduction(payload) {
    const response = await client.post('/api/v1/engineers/motivation/deductions/', payload);
    return response.data;
}


export async function loadAcceptanceMetadata() {
    const response = await client.get('/api/v1/acceptance/metadata/');
    return response.data;
}


export async function createAcceptanceTerritory(payload) {
    const formData = new FormData();
    formData.append('territory', payload.territory);
    formData.append('address', payload.address);

    const response = await client.post('/api/v1/acceptance/territories/', formData);
    return response.data;
}


export async function importAcceptanceModules(formData) {
    const response = await client.post('/api/v1/acceptance/modules/import/', formData);
    return response.data;
}


export async function importAcceptanceCassettes(formData) {
    const response = await client.post('/api/v1/acceptance/cassettes/import/', formData);
    return response.data;
}


export async function downloadAcceptanceTemplate(kind) {
    await downloadBlobResponse(
        client.get('/api/v1/acceptance/template/', {
            params: { kind },
            responseType: 'blob',
        }),
        `acceptance-${kind}.xlsx`,
    );
}


export async function loadAssemblyMetadata() {
    const response = await client.get('/api/v1/assembly/metadata/');
    return response.data;
}


export async function createAssemblyReserveRequest(payload) {
    const response = await client.post('/api/v1/assembly/requests/reserve/', payload);
    return response.data;
}


export async function createAssemblyCloseRequest(payload) {
    const response = await client.post('/api/v1/assembly/requests/close/', payload);
    return response.data;
}


export async function loadModuleBatchesMetadata(status) {
    const response = await client.get('/api/v1/module-batches/metadata/', {
        params: { status },
    });
    return response.data;
}


export async function createModuleBatchRequest(formData) {
    const response = await client.post('/api/v1/module-batches/requests/create/', formData);
    return response.data;
}


export async function loadCassetteBatchesMetadata() {
    const response = await client.get('/api/v1/cassettes/metadata/');
    return response.data;
}


export async function loadCassetteBatchDetail(territoryId, arrivalDate, numberKits, params = {}) {
    const response = await client.get(`/api/v1/cassettes/batches/${territoryId}/${arrivalDate}/${numberKits}/`, {
        params,
    });
    return response.data;
}


export async function downloadCassetteBatchExport(territoryId, arrivalDate, numberKits, params = {}) {
    await downloadBlobResponse(
        client.get(`/api/v1/cassettes/batches/${territoryId}/${arrivalDate}/${numberKits}/export/`, {
            params,
            responseType: 'blob',
        }),
        `cassette-batch-${territoryId}-${arrivalDate}-${numberKits}.xlsx`,
    );
}


export async function appendCassetteBatchItems(territoryId, arrivalDate, numberKits, formData) {
    const response = await client.post(`/api/v1/cassettes/batches/${territoryId}/${arrivalDate}/${numberKits}/append/`, formData);
    return response.data;
}


export async function deleteCassetteBatch(territoryId, arrivalDate, numberKits) {
    const response = await client.post(`/api/v1/cassettes/batches/${territoryId}/${arrivalDate}/${numberKits}/delete/`);
    return response.data;
}


export async function loadCassetteReplacementModules() {
    const response = await client.get('/api/v1/cassettes/replacement-modules/');
    return response.data;
}


export async function submitCassetteModuleAction(serialNumber, formData) {
    const response = await client.post(`/api/v1/cassettes/modules/${serialNumber}/action/`, formData);
    return response.data;
}


export async function submitCassetteBatchSend(territoryId, arrivalDate, numberKits, formData) {
    const response = await client.post(`/api/v1/cassettes/batches/${territoryId}/${arrivalDate}/${numberKits}/send/`, formData);
    return response.data;
}


export async function loadGuaranteeCassettes(params = {}) {
    const response = await client.get('/api/v1/cassettes/guarantee/', { params });
    return response.data;
}


export async function submitGuaranteeSend(formData) {
    const response = await client.post('/api/v1/cassettes/guarantee/send/', formData);
    return response.data;
}


export async function dispatchShipmentRequest(slug, formData) {
    const response = await client.post(`/api/v1/shipments/${slug}/dispatch/`, formData);
    return response.data;
}


export async function downloadLegacyDashboardExport(params = {}) {
    await downloadBlobResponse(
        client.get('/api/v1/dashboard/legacy/export/', {
            params,
            responseType: 'blob',
        }),
        'hyosung-dashboard.xlsx',
    );
}


export async function downloadWaitingActTemplate(slug) {
    await downloadBlobResponse(
        client.get(`/api/v1/acts/${slug}/request-template/`, {
            responseType: 'blob',
        }),
        `waiting-act-${slug}.xlsx`,
    );
}


export async function resendWaitingActRequest(slug) {
    const response = await client.post(`/api/v1/acts/${slug}/resend-request/`);
    return response.data;
}


export async function attachWaitingActFile(slug, formData) {
    const response = await client.post(`/api/v1/acts/${slug}/attach/`, formData);
    return response.data;
}
